import { createTheme } from '@mui/material/styles';
import { colors, typography, spacing, borders, shadows, touchTargets } from './design-tokens';

/**
 * Facto MUI Theme
 * WCAG 2.1 AA Compliant Theme Configuration
 * 
 * This theme implements all design system requirements:
 * - Single primary accent color (REQ-COL-01)
 * - WCAG compliant contrast ratios (REQ-COL-03)
 * - Off-white backgrounds (REQ-COL-04)
 * - Proper typography hierarchy (REQ-TYPO-04)
 * - Minimum font sizes (REQ-TYPO-02, REQ-TYPO-03)
 * - Line height ≥ 1.5 for body text (REQ-TYPO-05)
 * - Minimum touch targets 44×44px (REQ-CTA-05)
 */

const theme = createTheme({
  palette: {
    // Primary accent color - ONLY color used for primary CTAs (REQ-COL-01)
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
      contrastText: colors.primary.contrastText,
    },
    
    // Secondary removed - use neutral colors for secondary actions (REQ-COL-02)
    secondary: {
      main: colors.neutral[600],
      light: colors.neutral[400],
      dark: colors.neutral[800],
      contrastText: '#ffffff',
    },
    
    // Background colors - off-white default (REQ-COL-04)
    background: {
      default: colors.background.default,  // Off-white, not pure white
      paper: colors.background.paper,
    },
    
    // Text colors with WCAG compliant contrast (REQ-COL-03)
    text: {
      primary: colors.text.primary,     // 16.1:1 contrast ratio
      secondary: colors.text.secondary, // 7.0:1 contrast ratio
      disabled: colors.text.disabled,   // 3.8:1 contrast ratio
    },
    
    // Semantic colors
    success: {
      main: colors.success,
    },
    error: {
      main: colors.error,
    },
    warning: {
      main: colors.warning,
    },
    info: {
      main: colors.info,
    },
    
    // Neutral grays
    grey: colors.neutral,
  },
  
  typography: {
    // Font family - sans-serif for UI (REQ-TYPO-01)
    fontFamily: typography.fontFamily.body,
    
    // Base font size - 16px minimum (REQ-TYPO-02)
    fontSize: typography.fontSize.bodySmall,
    
    // Headings - clear visual hierarchy with display font (REQ-TYPO-04)
    h1: {
      fontFamily: typography.fontFamily.display,
      fontSize: `${typography.fontSize.h1}px`,
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      letterSpacing: typography.letterSpacing.tight,
      color: colors.text.primary,
    },
    h2: {
      fontFamily: typography.fontFamily.display,
      fontSize: `${typography.fontSize.h2}px`,
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      letterSpacing: typography.letterSpacing.tight,
      color: colors.text.primary,
    },
    h3: {
      fontFamily: typography.fontFamily.display,
      fontSize: `${typography.fontSize.h3}px`,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.tight,
      color: colors.text.primary,
    },
    h4: {
      fontFamily: typography.fontFamily.display,
      fontSize: `${typography.fontSize.h4}px`,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.normal,
      color: colors.text.primary,
    },
    h5: {
      fontFamily: typography.fontFamily.display,
      fontSize: `${typography.fontSize.h5}px`,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
      color: colors.text.primary,
    },
    h6: {
      fontFamily: typography.fontFamily.display,
      fontSize: `${typography.fontSize.h6}px`,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
      color: colors.text.primary,
    },
    
    // Body text - 18px recommended, line height ≥ 1.5 (REQ-TYPO-02, REQ-TYPO-05)
    body1: {
      fontSize: `${typography.fontSize.body}px`,
      lineHeight: typography.lineHeight.normal,
      color: colors.text.primary,
    },
    body2: {
      fontSize: `${typography.fontSize.bodySmall}px`,
      lineHeight: typography.lineHeight.normal,
      color: colors.text.tertiary,
    },
    
    // Button text - minimum 16px (REQ-TYPO-03)
    button: {
      fontSize: `${typography.fontSize.button}px`,
      fontWeight: typography.fontWeight.semibold,
      textTransform: 'none',
      lineHeight: typography.lineHeight.normal,
    },
    
    // Utility text
    caption: {
      fontSize: `${typography.fontSize.caption}px`,
      lineHeight: typography.lineHeight.normal,
      color: colors.text.tertiary,
    },
    overline: {
      fontSize: `${typography.fontSize.overline}px`,
      fontWeight: typography.fontWeight.semibold,
      textTransform: 'uppercase',
      letterSpacing: typography.letterSpacing.wide,
      lineHeight: typography.lineHeight.normal,
    },
  },
  
  spacing: spacing.unit,
  
  shape: {
    borderRadius: borders.radius.md,
  },
  
  components: {
    // Button component overrides
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borders.radius.md,
          minHeight: touchTargets.minimum, // Minimum 44px touch target (REQ-CTA-05)
          padding: `${spacing.md}px ${spacing.lg}px`,
          fontSize: `${typography.fontSize.button}px`,
          fontWeight: typography.fontWeight.semibold,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        sizeLarge: {
          minHeight: touchTargets.recommended, // 48px for large buttons
          padding: `${spacing.md}px ${spacing.xl}px`,
          fontSize: `${typography.fontSize.buttonLarge}px`,
        },
        sizeMedium: {
          minHeight: touchTargets.minimum,
          padding: `${spacing.sm}px ${spacing.lg}px`,
        },
        sizeSmall: {
          minHeight: touchTargets.minimum,
          padding: `${spacing.sm}px ${spacing.md}px`,
        },
        containedPrimary: {
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          '&:hover': {
            backgroundColor: colors.primary.dark,
          },
        },
        outlinedPrimary: {
          borderColor: colors.primary.main,
          borderWidth: borders.width.medium,
          color: colors.primary.main,
          '&:hover': {
            borderWidth: borders.width.medium,
            backgroundColor: `${colors.primary.main}10`,
          },
        },
        text: {
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: colors.neutral[100],
          },
        },
      },
    },
    
    // Paper component overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borders.radius.lg,
          backgroundColor: colors.background.paper,
        },
        elevation1: {
          boxShadow: shadows.sm,
        },
        elevation2: {
          boxShadow: shadows.md,
        },
        elevation3: {
          boxShadow: shadows.lg,
        },
      },
    },
    
    // AppBar component overrides - sticky navigation (REQ-NAV-01)
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `${borders.width.thin}px solid ${colors.neutral[300]}`,
          backgroundColor: colors.background.paper,
          color: colors.text.primary,
        },
      },
    },
    
    // Card component overrides
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borders.radius.lg,
          boxShadow: shadows.sm,
          '&:hover': {
            boxShadow: shadows.md,
          },
        },
      },
    },
    
    // CardContent component overrides - consistent padding (REQ-LAY-02)
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacing.card.padding,
          '&:last-child': {
            paddingBottom: spacing.card.padding,
          },
        },
      },
    },
    
    // Container component overrides
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: spacing.container.paddingX,
          paddingRight: spacing.container.paddingX,
          '@media (max-width: 600px)': {
            paddingLeft: spacing.container.paddingXMobile,
            paddingRight: spacing.container.paddingXMobile,
          },
        },
      },
    },
    
    // Typography component overrides
    MuiTypography: {
      styleOverrides: {
        paragraph: {
          marginBottom: spacing.md, // Paragraph spacing (REQ-TYPO-06)
        },
      },
    },
    
    // IconButton component overrides - minimum touch target
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: touchTargets.minimum,
          minHeight: touchTargets.minimum,
        },
      },
    },
    
    // Toolbar component overrides
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 64,
          '@media (min-width: 600px)': {
            minHeight: 64,
          },
        },
      },
    },
  },
});

export default theme;
